import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, ReferenceLine, CartesianGrid, XAxis, YAxis, Tooltip as ChartTooltip,
} from 'recharts';
import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { demoTheme } from '../../theme';
import { tarihEtiketi } from '../satis-analiz/ZamanBlok';
import { miktarMetni, oranMetni } from '../satis-analiz/bicim';
import { haftaGunleri } from '../satis-analiz/analizHesaplari';
import { HaftaGunleriKarti } from '../satis-analiz/GunDagilimBlok';
import { BlokIskeleti } from '../satis-analiz/BlokIskeleti';
import type { Donem } from '../satis-analiz/donem';
import { queryDataSource } from '../../api/query';
import type { StokKaynaklari } from './ekranKaynaklari';
import { stokParametreleri, stokQueryKey, useStokSorgu, type StokKaynak, type StokSorguFiltresi } from './useStokVerisi';
import { kayipCiroOrani, type KayipCiroSatiri } from './toplama';
import { kategoriNormalize } from './kategori';
import { SubeRozeti, subeListesi, type SubeGuvenilirlikSatiri } from './SubeRozeti';
import { kapsamNotuGorunsunMu } from './kapsam';
import { KartHatasi } from './KartHatasi';
import type { StokDetay } from './detay/stokDetayTipleri';

interface Props {
  kaynaklar: StokKaynaklari;
  donem: Donem;
  sube?: string | null;
  kategori?: string | null;
  /** C4'te StokDetay birliği genişletilecek; KAYIP bu bileşenden yalnız `kayipUrun`/`kayipGun` üretir. */
  ac?: (d: StokDetay) => void;
}

interface KayipOzetSatiri {
  tur: string; tur_adi: string; fis_sayisi: number; tutar: number; urun_sayisi?: number; miktar?: number; maliyet?: number;
}

/** K2: "Kayıp" başlığı ve blok altındaki "Sarf" notu — 990059'un tür kırılımından, tek sorgu. */
function fireSatiri(rows: KayipOzetSatiri[] | undefined): { tutar: number; fis: number } | null {
  const r = rows?.find((x) => x.tur === 'fire');
  return r ? { tutar: Number(r.tutar) || 0, fis: Number(r.fis_sayisi) || 0 } : null;
}

/** Fire ve sarf yan yana (990059): basligin "yalniz fire" kurali (K2) karti, sarf burada ayri satir. */
function KayipOzetKarti({ satirlar }: { satirlar: KayipOzetSatiri[] | undefined }) {
  if (!satirlar || satirlar.length === 0) return null;
  const turAdi = (r: KayipOzetSatiri) => (r.tur === 'fire' ? 'Fire' : r.tur === 'sarf' ? 'Sarf (içeride tüketilen)' : r.tur_adi);
  return (
    <div data-testid="kayip-ozet" className="min-w-0 flex flex-col">
      <SectionCard className="flex-1" title="Dönem özeti" subtitle="Fire ve sarf fişleri">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-[12px] text-ink-500 border-b border-border">
                <th className="text-left font-medium py-2 pr-3">Tür</th>
                <th className="text-right font-medium py-2 px-3">Fiş</th>
                <th className="text-right font-medium py-2 px-3">Ürün</th>
                <th className="text-right font-medium py-2 px-3">Miktar</th>
                <th className="text-right font-medium py-2 px-3">Tutar</th>
                <th className="text-right font-medium py-2 pl-3">Maliyet</th>
              </tr>
            </thead>
            <tbody>
              {satirlar.map((r) => (
                <tr key={r.tur} data-testid={r.tur === 'sarf' ? 'kayip-sarf-not' : `kayip-ozet-${r.tur}`} className="border-b border-border last:border-b-0">
                  <td className="py-2 pr-3 font-medium text-ink-900 whitespace-nowrap">{turAdi(r)}</td>
                  <td className="py-2 px-3 text-right tnum text-ink-500">{fmtNum(Number(r.fis_sayisi) || 0)}</td>
                  <td className="py-2 px-3 text-right tnum text-ink-500">{fmtNum(Number(r.urun_sayisi) || 0)}</td>
                  <td className="py-2 px-3 text-right tnum text-ink-500 whitespace-nowrap">{miktarMetni(Number(r.miktar) || 0)}</td>
                  <td className="py-2 px-3 text-right tnum text-ink-900 whitespace-nowrap">{fmtTRY(Number(r.tutar) || 0)}</td>
                  <td className="py-2 pl-3 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(Number(r.maliyet) || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

/** Fire'in haftanin gunlerine dagilimi (990060, Fire seyri ile ayni anahtar; ek istek yok). */
function FireGunleriKarti({ kaynak, f, tekGun }: { kaynak: StokKaynak | undefined; f: StokSorguFiltresi; tekGun: boolean }) {
  const q = useStokSorgu<SeyirSatiri>(kaynak, f);
  const gunler = useMemo(() => {
    const m = fireGunlukMap(q.data?.data ?? []);
    return haftaGunleri(Array.from(m.entries()).map(([gun, ciro]) => ({ tarih: String(gun).slice(0, 10), ciro })));
  }, [q.data]);
  if (!kaynak || tekGun || gunler.length < 2) return null;
  return <HaftaGunleriKarti testid="kayip-gunler" kok="kayip-gun" altBaslik="Fire girilen gün başına ortalama" gunler={gunler} />;
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 1 — Fire seyri (990060 + 990065)
// ════════════════════════════════════════════════════════════════════════════════════════════

interface SeyirSatiri { gun: string; tur: string; tutar: number }

/** 990060'ın tür kırılımından yalnız fire — günlük tutar haritası. */
function fireGunlukMap(rows: SeyirSatiri[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) {
    if (r.tur !== 'fire') continue;
    m.set(r.gun, (m.get(r.gun) ?? 0) + (Number(r.tutar) || 0));
  }
  return m;
}

interface GunNoktasi { kova: 'gun'; id: string; gun: string; etiket: string; fire: number; oran: number | null }
interface AyNoktasi { kova: 'ay'; id: string; ay: string; etiket: string; fire: number; oran: number | null }
type SeyirGrafikNoktasi = GunNoktasi | AyNoktasi;

/**
 * Düzeltme turu 1/5 (orkestratör kararı 2026-09-16) — seçilen aralık bu eşikten uzunsa "Fire seyri"
 * günlük yerine AYLIK kovalar: varsayılan dönem (bir ay, ~30 gün) günlük kalır, "Bu yıl" gibi 250+
 * günlük pencerelerde günlük çubuk okunmaz olurdu. 62 gün ≈ iki ay; sabit, keyfi değil — orkestratörün
 * verdiği eşik burada TEK yerde tutulur (KayipBlok.test.tsx `donemGunSayisi` testiyle kapılanır).
 */
export const AYLIK_ESIK_GUN = 62;

export function donemGunSayisi(donem: Donem): number {
  return dayjs(donem.bit).diff(dayjs(donem.bas), 'day') + 1;
}

const AY_KISALTMALARI = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

/**
 * `dayjs` locale AYARLANMAMIŞ (donem.ts:68 notu) — `.format('MMM')` İngilizce ay adı basardı. Türkçe
 * kısaltma bu yüzden elle: tarihEtiketi'nin (ZamanBlok) GG.AA.YYYY kalıbıyla aynı gerekçe.
 */
export function ayEtiketi(ay: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ay);
  if (!m) return ay;
  const kisa = AY_KISALTMALARI[Number(m[2]) - 1] ?? ay;
  return `${kisa} ${m[1]}`;
}

/**
 * 990065 GÜN OMURGASIDIR (FULL OUTER — fire'sız ciro günü de burada). 990060'ın fire tutarı önceliklidir;
 * 990060 o günü hiç içermiyorsa (ya da ds kaynağı yoksa) 990065'in kendi `fire_tutar`ı yedek olarak kullanılır
 * (ikisi aynı matview'dan geldiği için sayı aynıdır — 990060 salt ds kesintisine karşı dayanıklılık sağlar).
 */
export function gunSeyriNoktalari(ciro: KayipCiroSatiri[], fireMap: Map<string, number>): GunNoktasi[] {
  return ciro.map((c) => {
    const fire = fireMap.has(c.gun) ? (fireMap.get(c.gun) as number) : Number(c.fire_tutar) || 0;
    const ciroTutar = Number(c.ciro_tutar) || 0;
    return {
      kova: 'gun', id: c.gun, gun: c.gun, etiket: tarihEtiketi(c.gun), fire,
      oran: ciroTutar > 0 ? (100 * fire) / ciroTutar : null,
    };
  });
}

/**
 * 🔴 Kova oranı ORTALAMA DEĞİL: her ayda Σfire ve Σciro AYRI toplanır, SONRA bölünür (`kayipCiroOrani`
 * ile aynı mantık — günlük oranların ortalaması alınsaydı fire'sız/cirosuz günler oranı çarpıtırdı).
 */
export function aySeyriNoktalari(ciro: KayipCiroSatiri[], fireMap: Map<string, number>): AyNoktasi[] {
  const gruplar = new Map<string, { fire: number; ciro: number }>();
  for (const c of ciro) {
    const ay = c.gun.slice(0, 7);
    const fire = fireMap.has(c.gun) ? (fireMap.get(c.gun) as number) : Number(c.fire_tutar) || 0;
    const ciroTutar = Number(c.ciro_tutar) || 0;
    const g = gruplar.get(ay) ?? { fire: 0, ciro: 0 };
    g.fire += fire;
    g.ciro += ciroTutar;
    gruplar.set(ay, g);
  }
  return Array.from(gruplar.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([ay, g]) => ({
      kova: 'ay', id: ay, ay, etiket: ayEtiketi(ay), fire: g.fire,
      oran: g.ciro > 0 ? (100 * g.fire) / g.ciro : null,
    }));
}

function noktaDetayi(n: SeyirGrafikNoktasi): StokDetay {
  return n.kova === 'gun' ? { tur: 'kayipGun', kova: 'gun', gun: n.gun } : { tur: 'kayipGun', kova: 'ay', ay: n.ay };
}

function FireSeyriKarti({
  kaynaklar, f, ac,
}: {
  kaynaklar: Pick<StokKaynaklari, 'kayipSeyir' | 'kayipCiro'>;
  f: StokSorguFiltresi;
  ac?: (d: StokDetay) => void;
}) {
  const seyirQ = useStokSorgu<SeyirSatiri>(kaynaklar.kayipSeyir, f);
  const ciroQ = useStokSorgu<KayipCiroSatiri>(kaynaklar.kayipCiro, f);

  // 990065 zorunlu (gün omurgası + oranı); 990060 yalnız bar değerini doğrular, yoksa 990065 yedeği kullanılır.
  if (!kaynaklar.kayipCiro) return null;
  if (ciroQ.isLoading) {
    return <BlokIskeleti testid="kayip-seyir-iskelet" baslik="Fire seyri" yukseklik={260} />;
  }
  if (ciroQ.isError) {
    return <KartHatasi testid="kayip-seyir-hata" baslik="Fire seyri" yukseklik={260} mesaj="Fire seyri yüklenemedi." />;
  }
  if (ciroQ.data === undefined) return null;

  const ciroSatirlar = ciroQ.data.data;
  const fireMap = fireGunlukMap(seyirQ.data?.data ?? []);
  // Düzeltme turu 1/5: 62 günden uzun pencerede AYLIK kovala (250+ günlük çubuk okunmaz).
  const aylikMi = donemGunSayisi(f.donem) > AYLIK_ESIK_GUN;
  const noktalar: SeyirGrafikNoktasi[] = aylikMi
    ? aySeyriNoktalari(ciroSatirlar, fireMap)
    : gunSeyriNoktalari(ciroSatirlar, fireMap);
  if (noktalar.length === 0) return null;

  const ortalamaOran = kayipCiroOrani(ciroSatirlar).oran;
  const tiklanabilir = !!ac;

  return (
    <div data-testid="kayip-seyir" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="Fire seyri"
        subtitle={
          <>
            {aylikMi ? 'Aylık' : 'Günlük'} fire ve ciroya oranı
            {ortalamaOran !== null && (
              <>
                {' '}
                · ortalama <span data-testid="kayip-seyir-ortalama">{oranMetni(ortalamaOran, 2)}</span>
              </>
            )}
          </>
        }
      >
        {/* flex-1 min-h: sabit yukseklikli grafik esit-boy zincirinde bosluk birakir (2026-09-15 dersi). */}
        <div data-testid="kayip-seyir-grafik" className="flex-1 min-h-[220px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={noktalar} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={demoTheme.border} vertical={false} />
              <XAxis
                dataKey="etiket"
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={{ stroke: demoTheme.border }}
                tickLine={false}
                minTickGap={24}
                interval="preserveStartEnd"
              />
              <YAxis
                yAxisId="fire"
                tickFormatter={(v) => fmtNum(Number(v))}
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={false}
                tickLine={false}
                width={64}
              />
              <YAxis
                yAxisId="oran"
                orientation="right"
                tickFormatter={(v) => `%${Number(v).toFixed(0)}`}
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={false}
                tickLine={false}
                width={44}
              />
              <ChartTooltip
                formatter={(v, ad) => (ad === 'oran' ? `${oranMetni(Number(v), 2)}` : fmtTRY(Number(v)))}
                labelFormatter={(l) => String(l)}
              />
              <Bar
                yAxisId="fire"
                dataKey="fire"
                name="Fire"
                fill={demoTheme.negative}
                radius={[2, 2, 0, 0]}
                isAnimationActive={false}
                cursor={tiklanabilir ? 'pointer' : undefined}
                onClick={
                  tiklanabilir
                    ? (_veri, i) => {
                        const n = noktalar[i];
                        if (n) ac?.(noktaDetayi(n));
                      }
                    : undefined
                }
              />
              <Line
                yAxisId="oran"
                dataKey="oran"
                name="oran"
                stroke={demoTheme.primary}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
              {ortalamaOran !== null && (
                <ReferenceLine yAxisId="oran" y={ortalamaOran} stroke={demoTheme.primary} strokeDasharray="4 4" />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {/* Cubuk tiklamasi fareye ozgu; ZamanBlok ile ayni erisilebilirlik kalibi: gizli, odakta gorunur liste. */}
        {tiklanabilir && (
          <ul
            data-testid="kayip-seyir-nokta-listesi"
            aria-label={aylikMi ? 'Ay ayrıntısı' : 'Gün ayrıntısı'}
            className="sr-only focus-within:not-sr-only focus-within:mt-2 focus-within:flex focus-within:flex-wrap focus-within:gap-1"
          >
            {noktalar.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  data-testid={`kayip-seyir-${n.kova}-${n.id}`}
                  aria-label={`${n.etiket} ${n.kova === 'ay' ? 'ayı' : 'günü'} ayrıntısı`}
                  onClick={() => ac?.(noktaDetayi(n))}
                  className="focus-ring text-[11px] px-1.5 py-0.5 rounded border border-border text-ink-700"
                >
                  {n.etiket}
                </button>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 2 — Fire nerede (990061) — sıralı liste + pay, ÇUBUK DEĞİL
// ════════════════════════════════════════════════════════════════════════════════════════════

interface KategoriSatiri { kategori: string; tur: string; urun_sayisi: number; tutar: number }
interface KategoriPayi { kategori: string; urunSayisi: number; tutar: number; pay: number | null }

/** Pay istemcide hesaplanır (990061 `pay` dönmez): Σtutar bu yanıttaki fire satırlarınındır. */
function kategoriPaylari(rows: KategoriSatiri[]): KategoriPayi[] {
  const fireRows = rows.filter((r) => r.tur === 'fire');
  const toplam = fireRows.reduce((t, r) => t + (Number(r.tutar) || 0), 0);
  return [...fireRows]
    .sort((a, b) => (Number(b.tutar) || 0) - (Number(a.tutar) || 0))
    .map((r) => ({
      kategori: kategoriNormalize(r.kategori),
      urunSayisi: Number(r.urun_sayisi) || 0,
      tutar: Number(r.tutar) || 0,
      pay: toplam > 0 ? (Number(r.tutar) / toplam) * 100 : null,
    }));
}

const KATEGORI_KAPSAM_NOT_METNI =
  'Kategori süzgeci bu kartta geçerli değil — bu kart kategori süzgecinden etkilenmez, tüm kategorileri gösterir.';

function KategoriKarti({
  kaynak, f, kategori,
}: {
  kaynak: StokKaynak | undefined;
  f: StokSorguFiltresi;
  /**
   * I-1 (final-fix F-2) — 990061 `@KATEGORI` ne bildirir ne kullanır (144-gizova-kayip-ds.sql:92-104):
   * kategori süzgeci doluyken bu kart SÜZÜLMEDEN kalır (bütün kategoriler görünür) — modal içindeki
   * `kategoriNormalize`li süzülmüş listeyle KARIŞTIRILMASIN diye burada AÇIKÇA not düşülür.
   */
  kategori: string | null;
}) {
  const q = useStokSorgu<KategoriSatiri>(kaynak, f);
  if (!kaynak) return null;
  if (q.isLoading) {
    return <BlokIskeleti testid="kayip-kategori-iskelet" baslik="Fire nerede" yukseklik={200} satir={5} />;
  }
  if (q.isError) {
    return <KartHatasi testid="kayip-kategori-hata" baslik="Fire nerede" yukseklik={200} mesaj="Fire nerede yüklenemedi." />;
  }
  if (q.data === undefined) return null;
  const satirlar = kategoriPaylari(q.data.data);
  if (satirlar.length === 0) return null;

  const kapsamNotuGorunur = kapsamNotuGorunsunMu([kaynak], '@KATEGORI', kategori);

  return (
    <div data-testid="kayip-kategori" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="Fire nerede"
        subtitle="Kategoriye göre pay — tek kategori baskınken çubuk yanıltır"
      >
        {kapsamNotuGorunur && (
          <p data-testid="kayip-kategori-kapsam-not" className="text-[11px] text-ink-400 mb-2">
            {KATEGORI_KAPSAM_NOT_METNI}
          </p>
        )}
        <ul className="m-0 p-0 flex flex-col gap-2">
          {satirlar.map((s) => (
            <li
              key={s.kategori}
              data-testid={`kayip-kategori-satir-${s.kategori}`}
              className="flex items-center justify-between gap-3 text-[13px]"
            >
              <span className="text-ink-900 truncate min-w-0">{s.kategori}</span>
              <span className="flex items-center gap-3 shrink-0 tnum">
                <span className="text-ink-500">{fmtNum(s.urunSayisi)} ürün</span>
                <span className="text-ink-700">{fmtTRY(s.tutar)}</span>
                <span className="text-ink-900 font-medium w-16 text-right">
                  {s.pay === null ? '—' : oranMetni(s.pay, 2)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 3 — Şube (990059 lokasyon başına + 990050 lokasyon listesi) — C1-2/C1-3 ruling
// ════════════════════════════════════════════════════════════════════════════════════════════

interface SubeSatiri { lokasyon: string; tutar: number; fis: number }

function SubeKarti({
  kaynaklar, f, ozetVeri, ozetYukleniyor, ozetHata,
}: {
  kaynaklar: Pick<StokKaynaklari, 'kayipOzet' | 'subeGuvenilirlik'>;
  f: StokSorguFiltresi;
  /** Blok başlığının 990059 sonucu — AYNI sorgu, tekrar çekilmez (C1-3). */
  ozetVeri: KayipOzetSatiri[] | undefined;
  /** F-4 — `ozetVeri === undefined` YÜKLENİYOR ile HATA'yı ayırt edemiyordu; sorgu durumu açıkça geçilir. */
  ozetYukleniyor: boolean;
  ozetHata: boolean;
}) {
  const sube = f.sube ?? null;
  // I-2 (final-fix F-3) — ESKİDEN yalnız `sube === null`iken ENABLED idi: `sube` doluyken bu sorgu
  // hiç ATEŞLENMİYORDU, yani köşe rozeti veri bekleyip SESSİZCE boş kalıyordu (§4.5 hiçbir zaman
  // görünmezdi). Artık HER ZAMAN açık — 990050 zaten parametresiz (fixture: `subeGuvenilirlik: []`),
  // `StokFiltreSeridi`nin AYNI sorgusuyla query-key TEKİLLEŞİR, yeni ağ isteği açılmaz.
  const listeQ = useStokSorgu<SubeGuvenilirlikSatiri>(kaynaklar.subeGuvenilirlik, f, true);
  const lokasyonlar = useMemo(() => subeListesi(listeQ.data?.data ?? []), [listeQ.data]);

  const kayipOzetKaynak = kaynaklar.kayipOzet;
  // C1-3: sube DOLUYSA bu dizi bos -> useQueries hic istek atmaz (0 ek istek).
  const lokasyonSorgulari = useQueries({
    queries: (sube === null ? lokasyonlar : []).map((lok) => {
      const parametreler = kayipOzetKaynak ? stokParametreleri(kayipOzetKaynak, { ...f, sube: lok }) : {};
      return {
        queryKey: stokQueryKey(kayipOzetKaynak?.sourceId, parametreler),
        queryFn: () => queryDataSource<KayipOzetSatiri>({ sourceId: kayipOzetKaynak!.sourceId, parameters: parametreler }),
        enabled: !!kayipOzetKaynak,
        throwOnError: false as const,
      };
    }),
  });

  if (!kaynaklar.kayipOzet) return null;

  const listeYukleniyor = !!kaynaklar.subeGuvenilirlik && listeQ.isLoading;
  const kirilimYukleniyor = sube === null && lokasyonlar.length > 0 && lokasyonSorgulari.some((q) => q.isLoading);
  const yukleniyor = (sube !== null ? ozetYukleniyor : kirilimYukleniyor) || listeYukleniyor;

  if (yukleniyor) return <BlokIskeleti testid="kayip-sube-iskelet" baslik="Şube" yukseklik={160} satir={3} />;

  // F-4 — hata sube!==null'de ozetQ'dan (headline fire), sube===null'de kırılım sorgularından gelir.
  const kirilimHata = sube === null && lokasyonSorgulari.some((q) => q.isError);
  const hata = (sube !== null ? ozetHata : kirilimHata) || listeQ.isError;
  if (hata) return <KartHatasi testid="kayip-sube-hata" baslik="Şube" yukseklik={160} mesaj="Şube kırılımı yüklenemedi." />;

  let satirlar: SubeSatiri[];
  if (sube !== null) {
    const s = fireSatiri(ozetVeri);
    satirlar = s ? [{ lokasyon: sube, tutar: s.tutar, fis: s.fis }] : [];
  } else {
    satirlar = lokasyonlar.map((lok, i) => {
      const s = fireSatiri(lokasyonSorgulari[i]?.data?.data);
      return { lokasyon: lok, tutar: s?.tutar ?? 0, fis: s?.fis ?? 0 };
    });
  }
  if (satirlar.length === 0) return null;

  // C1-2 (son cümle): lokasyon kırılımı toplamı blok toplamıyla tutmazsa GİZLENMEZ, not düşülür.
  const genelFire = sube === null ? fireSatiri(ozetVeri) : null;
  const kirilimToplami = satirlar.reduce((t, s) => t + s.tutar, 0);
  const farkVar = genelFire !== null && Math.abs(kirilimToplami - genelFire.tutar) > 1;
  const subeSatirlari = listeQ.data?.data ?? [];

  return (
    <div data-testid="kayip-sube" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="Şube"
        subtitle="Fire tutarı ve fiş sayısı — ikisi birlikte (tutar ~2 kat iken fiş ≈ eşit olabilir)"
      >
        <table className="w-full text-[13px] border-collapse">
          <thead>
            <tr className="text-[12px] text-ink-500 border-b border-border">
              <th className="text-left font-medium py-2 pr-3">Şube</th>
              <th className="text-right font-medium py-2 px-3">Tutar</th>
              <th className="text-right font-medium py-2 pl-3">Fiş</th>
            </tr>
          </thead>
          <tbody>
            {satirlar.map((s) => (
              <tr key={s.lokasyon} data-testid={`kayip-sube-satir-${s.lokasyon}`} className="border-b border-border last:border-b-0">
                <td className="py-2 pr-3 text-ink-900">
                  {/* I-2 (final-fix F-3) — §4.5 rozeti KIRILIM SATIRI BAŞINA: "ATAKENT'te fire 2 katı"
                      okuyan kişi, o şubenin stok kaydının da şüpheli olduğunu AYNI SATIRDA görür.
                      Eskiden köşede TEK rozet vardı (yalnız süzgeçli görünümde, yanlış koşul) —
                      varsayılan (süzgeçsiz) ekranda 712 üründeki uyarı hiç görünmüyordu. */}
                  <span className="inline-flex items-center gap-1.5">
                    {s.lokasyon}
                    {/* Varsayılan testid'i KORUNUR (`sube-rozet-<lokasyon>`) — StokFiltreSeridi.test.tsx
                        ve StokAnalizi.test.tsx'in mevcut `sube-rozet-ATAKENT` beklentisiyle AYNI kalır. */}
                    <SubeRozeti sube={s.lokasyon} satirlar={subeSatirlari} />
                  </span>
                </td>
                <td className="py-2 px-3 text-right tnum text-ink-900 whitespace-nowrap">{fmtTRY(s.tutar)}</td>
                <td className="py-2 pl-3 text-right tnum text-ink-500 whitespace-nowrap">{fmtNum(s.fis)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {farkVar && genelFire && (
          <p data-testid="kayip-sube-fark-notu" className="text-[12px] text-ink-500 mt-2">
            {`Lokasyon kırılımı toplamı (${fmtTRY(kirilimToplami)}) blok toplamından (${fmtTRY(genelFire.tutar)}) farklı — 990050'de görünmeyen bir lokasyonda fire olabilir.`}
          </p>
        )}
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 4 — En çok fire veren ürünler (990062, @TUR=fire)
// ════════════════════════════════════════════════════════════════════════════════════════════

/**
 * Düzeltme turu 1/5 (orkestratör kararı 2026-09-16) — GERÇEK 990062 kolonları
 * (`cdc-reports/sql/vega/144-gizova-kayip-ds.sql:127-133`): `stok_kodu, urun, kategori, miktar,
 * tutar, maliyet, kumulatif_pay`. Brief'in ilk verdiği `stok_kod/stok_ad/pay/kum_pay` adları
 * UYDURMAYDI — burada gerçek adlara remap edildi. `kumulatif_pay` bir ORAN (0..1, `sum(...) OVER
 * (...) / nullif(sum(...) OVER (), 0)`), yüzde DEĞİL. Ürün başına `pay` kolonu YOKTUR — istemcide
 * `tutar / Σtutar` ile hesaplanır (Σ = bu yanıttaki TÜM satırlar; ds `ORDER BY tutar DESC`, LIMIT yok).
 */
interface UrunSatiriHam {
  stok_kodu: string;
  urun: string;
  tutar: number;
  kumulatif_pay: number; // 0..1
}

interface UrunPayi { stokKod: string; stokAd: string; tutar: number; pay: number | null; kumulatifPay: number }

function urunPaylari(rows: UrunSatiriHam[]): UrunPayi[] {
  const toplam = rows.reduce((t, r) => t + (Number(r.tutar) || 0), 0);
  return rows.map((r) => ({
    stokKod: String(r.stok_kodu),
    stokAd: String(r.urun),
    tutar: Number(r.tutar) || 0,
    pay: toplam > 0 ? (Number(r.tutar) / toplam) * 100 : null,
    kumulatifPay: Number(r.kumulatif_pay) || 0,
  }));
}

const URUN_ILK_N = 10;
const URUN_ESIK_ORAN = 0.8; // kumulatif_pay 0..1 tabanında — TEK kaynak, metin buradan türer.
// Düzeltme turu 2/5: iki bağımsız sabit ayrışabilirdi (biri güncellenip diğeri unutulursa "%80'i"
// metni gerçek eşikle sessizce çelişirdi) — metin sabiti artık ORANDAN türetiliyor.
const URUN_ESIK_YUZDE = Math.round(URUN_ESIK_ORAN * 100);

function ilkYuzdeSayisi(rows: UrunPayi[], esikOran = URUN_ESIK_ORAN): number | null {
  const i = rows.findIndex((r) => r.kumulatifPay >= esikOran);
  return i === -1 ? null : i + 1;
}

function UrunKarti({
  kaynak, f, ac,
}: {
  kaynak: StokKaynak | undefined;
  f: StokSorguFiltresi;
  ac?: (d: StokDetay) => void;
}) {
  // 🔴 K2 — @TUR='fire' ZORUNLU: gecilmezse sarf listeye karisir (990062 @TUR bildiriyor, R1).
  const q = useStokSorgu<UrunSatiriHam>(kaynak, { ...f, tur: 'fire' });
  if (!kaynak) return null;
  if (q.isLoading) {
    return <BlokIskeleti testid="kayip-urun-iskelet" baslik="En çok fire veren ürünler" yukseklik={260} satir={6} />;
  }
  if (q.isError) {
    return (
      <KartHatasi testid="kayip-urun-hata" baslik="En çok fire veren ürünler" yukseklik={260} mesaj="Ürün kırılımı yüklenemedi." />
    );
  }
  if (q.data === undefined) return null;
  const tumSatirlar = urunPaylari(q.data.data);
  const satirlar = tumSatirlar.slice(0, URUN_ILK_N);
  const not80 = ilkYuzdeSayisi(tumSatirlar);
  if (satirlar.length === 0) return null;

  return (
    <div data-testid="kayip-urun" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="En çok fire veren ürünler"
        subtitle="İlk 10 · tutara göre"
        actions={
          ac && (
            <button
              type="button"
              data-testid="kayip-urun-tumu"
              onClick={() => ac({ tur: 'kayipTumu' })}
              className="focus-ring rounded-md border border-border px-2.5 py-1 text-[12px] text-ink-700 hover:text-ink-900"
            >
              Tüm ürünler
            </button>
          )
        }
      >
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-ink-400 text-left">
              <th className="font-normal py-1">Ürün</th>
              <th className="font-normal py-1 text-right">Tutar</th>
              <th className="font-normal py-1 text-right">Pay</th>
            </tr>
          </thead>
          <tbody>
            {satirlar.map((r) => (
              <tr key={r.stokKod} data-testid={`kayip-urun-satir-${r.stokKod}`} className="border-t border-border">
                <td className="py-1 pr-2 text-ink-800 truncate max-w-0 w-full">
                  {ac ? (
                    <button
                      type="button"
                      data-testid={`kayip-urun-detay-${r.stokKod}`}
                      onClick={() => ac({ tur: 'kayipUrun', stokKod: r.stokKod, stokAd: r.stokAd })}
                      className="focus-ring block w-full truncate text-left hover:underline underline-offset-2"
                    >
                      {r.stokAd}
                    </button>
                  ) : (
                    r.stokAd
                  )}
                </td>
                <td className="py-1 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(r.tutar)}</td>
                <td className="py-1 pl-2 text-right tnum text-ink-500 whitespace-nowrap">
                  {r.pay === null ? '—' : oranMetni(r.pay, 2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {not80 !== null && (
          <p data-testid="kayip-urun-not" className="text-[12px] text-ink-500 mt-2">
            {`${not80} ürün fire'ın %${URUN_ESIK_YUZDE}'i`}
          </p>
        )}
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Blok — KAYIP (§4.2)
// ════════════════════════════════════════════════════════════════════════════════════════════

/**
 * Spec §4.2: dört kart (seyir/kategori/şube/ürün) + sarf alt notu. Başlık ve tutar ekranda sekme + Fire kartında.
 * Ağustos sıçraması için ekrana yorum YAZILMAZ (kullanıcı kararı 2026-09-16, ölçülmedi); sıçrayan
 * çubuk/ay tıklanabilir (`ac({tur:'kayipGun', kova:'gun', gun})` ya da uzun pencerede
 * `ac({tur:'kayipGun', kova:'ay', ay})`), yorum burada da eklenmez.
 */
export function KayipBlok({ kaynaklar, donem, sube = null, kategori = null, ac }: Props) {
  const f: StokSorguFiltresi = { donem, sube, kategori };
  const ozetQ = useStokSorgu<KayipOzetSatiri>(kaynaklar.kayipOzet, f);

  const hicKaynakYok =
    !kaynaklar.kayipOzet && !kaynaklar.kayipSeyir && !kaynaklar.kayipCiro &&
    !kaynaklar.kayipKategori && !kaynaklar.subeGuvenilirlik && !kaynaklar.kayipUrun;
  if (hicKaynakYok) return null;

  const ozetVeri = ozetQ.data?.data;

  return (
    <section data-testid="kayip-blok" className="flex flex-col gap-3">
      <div data-testid="kayip-ust" className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-3 items-stretch">
        <KayipOzetKarti satirlar={ozetVeri} />
        <FireGunleriKarti kaynak={kaynaklar.kayipSeyir} f={f} tekGun={donem.tekGun} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
        <FireSeyriKarti kaynaklar={kaynaklar} f={f} ac={ac} />
        <KategoriKarti kaynak={kaynaklar.kayipKategori} f={f} kategori={kategori} />
        <SubeKarti
          kaynaklar={kaynaklar}
          f={f}
          ozetVeri={ozetVeri}
          ozetYukleniyor={ozetQ.isLoading}
          ozetHata={ozetQ.isError}
        />
        <UrunKarti kaynak={kaynaklar.kayipUrun} f={f} ac={ac} />
      </div>

    </section>
  );
}
